import { useCallback, useEffect, useMemo, useState } from 'react';
import { Plus, ArrowLeft, FileText, Briefcase, Lightbulb, BarChart3 } from 'lucide-react';
import Card from '../ui/Card';
import Button from '../ui/Button';
import Avatar from '../ui/Avatar';
import { Skeleton } from '../Skeleton';
import {
    getSubmissions,
    listMentorshipProjects,
    listReports,
    MODULE_LABELS,
    type Submission,
    type Report,
    type MentorshipProject,
} from '../../mentorshipService';
import type { BackendUserWithPlan } from '../../userService';
import SubmissionModal from './SubmissionModal';
import ReportViewer from './ReportViewer';
import RecommendationsList from './RecommendationsList';
import ProgressionTimeline from './ProgressionTimeline';
import NotificationsBell from '../NotificationsBell/NotificationsBell';
import './MenteeMentorshipView.css';
import './mentorship.css';

type Tab = 'history' | 'recommendations' | 'progression';

type Props = {
    user: BackendUserWithPlan;
};

function MenteeMentorshipView({ user }: Props) {
    const [submissions, setSubmissions] = useState<Submission[] | null>(null);
    const [projects, setProjects] = useState<MentorshipProject[] | null>(null);
    const [reports, setReports] = useState<Report[] | null>(null);
    const [loading, setLoading] = useState(true);
    const [showSubmitModal, setShowSubmitModal] = useState(false);
    const [activeTab, setActiveTab] = useState<Tab>('history');
    const [openReport, setOpenReport] = useState<Report | null>(null);

    /** Fetch every dashboard data source in parallel.
     *
     * Pass an `isCancelled` checker (from the mount effect) so we don't call
     * setState after unmount; outside the mount effect (e.g. after a new
     * submission lands) callers pass `undefined` and writes always commit.
     */
    const loadDashboard = useCallback(
        async (isCancelled?: () => boolean) => {
            setLoading(true);
            try {
                const [subPage, projPage, reportPage] = await Promise.all([
                    getSubmissions(),
                    listMentorshipProjects({ active: true }),
                    listReports({ mentee_id: user.id }),
                ]);
                if (isCancelled?.()) return;
                setSubmissions(subPage.results);
                setProjects(projPage.results);
                setReports(reportPage.results);
            } catch (err) {
                console.error(
                    '[MenteeMentorshipView] failed to load dashboard data',
                    err,
                );
                if (isCancelled?.()) return;
                setSubmissions([]);
                setProjects([]);
                setReports([]);
            } finally {
                if (!isCancelled?.()) setLoading(false);
            }
        },
        [user.id],
    );

    useEffect(() => {
        let cancelled = false;
        loadDashboard(() => cancelled);
        return () => {
            cancelled = true;
        };
    }, [loadDashboard]);

    const handleSubmissionCreated = () => {
        setShowSubmitModal(false);
        // Re-fetch so the new submission appears in the history list
        void loadDashboard();
    };

    // Latest report per module — used to know which submissions have been
    // reviewed and which are still in flight
    const latestReportByModule = useMemo(() => {
        const map = new Map<string, Report>();
        for (const r of reports ?? []) {
            const key = `${r.submission_summary.module}-${r.submission_summary.quarter}`;
            const existing = map.get(key);
            if (!existing || r.version > existing.version) {
                map.set(key, r);
            }
        }
        return map;
    }, [reports]);

    if (openReport) {
        return (
            <section className="mentorship-view">
                <header className="mentorship-view__header">
                    <button
                        type="button"
                        className="mentorship-stack__back"
                        onClick={() => setOpenReport(null)}
                        aria-label="Back to dashboard"
                    >
                        <ArrowLeft size={18} />
                        Back to dashboard
                    </button>
                </header>
                <div className="mentorship-view__content">
                    <ReportViewer report={openReport} />
                </div>
            </section>
        );
    }

    return (
        <section className="mentorship-view">
            <header className="mentorship-view__header">
                <div>
                    <h2 className="mentorship-view__title">
                        Coaching · {user.first_name || user.username}
                    </h2>
                    <p className="mentorship-view__subtitle">
                        Your mentorship dashboard.
                    </p>
                </div>
                <Button
                    variant="primary"
                    size="md"
                    icon={<Plus size={16} />}
                    onClick={() => setShowSubmitModal(true)}
                >
                    New submission
                </Button>
                <NotificationsBell />
            </header>

            <div className="mentorship-view__content">
                {/* Your mentor card */}
                <Card variant="row" className="mentee-dashboard__mentor-card">
                    <Card.Header>
                        {(() => {
                            const summary = user.profile?.mentor_summary;
                            const mentorId = user.profile?.mentor_id;
                            const fullName = summary
                                ? `${summary.first_name} ${summary.last_name}`.trim() ||
                                  summary.email ||
                                  `Mentor #${summary.id}`
                                : null;
                            return (
                                <>
                                    <Avatar
                                        name={fullName ?? (mentorId ? `Mentor ${mentorId}` : '?')}
                                        size="md"
                                    />
                                    <div>
                                        <p className="mentee-dashboard__mentor-label">
                                            Your mentor
                                        </p>
                                        {summary ? (
                                            <>
                                                <p className="mentee-dashboard__mentor-name">
                                                    {fullName}
                                                </p>
                                                {summary.email && (
                                                    <p className="mentee-dashboard__mentor-email">
                                                        {summary.email}
                                                    </p>
                                                )}
                                            </>
                                        ) : mentorId ? (
                                            <p className="mentee-dashboard__mentor-name">
                                                Mentor #{mentorId}
                                            </p>
                                        ) : (
                                            <p className="mentee-dashboard__mentor-name mentee-dashboard__mentor-name--unset">
                                                Will be assigned when you submit
                                            </p>
                                        )}
                                    </div>
                                </>
                            );
                        })()}
                    </Card.Header>
                </Card>

                {/* Active projects */}
                <section className="mentee-dashboard__section">
                    <h3 className="mentee-dashboard__section-title">
                        <Briefcase size={18} /> Your active projects
                    </h3>
                    {loading ? (
                        <Skeleton width="100%" height="64px" borderRadius="12px" />
                    ) : !projects || projects.length === 0 ? (
                        <p className="mentee-dashboard__empty-line">
                            No active projects yet. Your mentor will set one up when you
                            start a new engagement.
                        </p>
                    ) : (
                        <ul className="mentee-dashboard__project-list">
                            {projects.map((p) => (
                                <li key={p.id}>
                                    <Card variant="row">
                                        <Card.Header>
                                            <div>
                                                <p className="mentee-dashboard__project-name">
                                                    {p.name}
                                                </p>
                                                {p.client_id && (
                                                    <p className="mentee-dashboard__project-client">
                                                        Client: {p.client_id}
                                                    </p>
                                                )}
                                                <p className="mentee-dashboard__project-meta">
                                                    {p.risks_count}{' '}
                                                    {p.risks_count === 1 ? 'risk' : 'risks'} curated
                                                </p>
                                            </div>
                                        </Card.Header>
                                    </Card>
                                </li>
                            ))}
                        </ul>
                    )}
                </section>

                {/* Tab strip */}
                <nav
                    className="mentorship-tabs"
                    role="tablist"
                    aria-label="Mentorship dashboard tabs"
                >
                    <button
                        type="button"
                        role="tab"
                        aria-selected={activeTab === 'history' ? 'true' : 'false'}
                        className={`mentorship-tab ${activeTab === 'history' ? 'is-active' : ''}`}
                        onClick={() => setActiveTab('history')}
                    >
                        <FileText size={16} /> History
                    </button>
                    <button
                        type="button"
                        role="tab"
                        aria-selected={activeTab === 'recommendations' ? 'true' : 'false'}
                        className={`mentorship-tab ${
                            activeTab === 'recommendations' ? 'is-active' : ''
                        }`}
                        onClick={() => setActiveTab('recommendations')}
                    >
                        <Lightbulb size={16} /> Recommendations
                    </button>
                    <button
                        type="button"
                        role="tab"
                        aria-selected={activeTab === 'progression' ? 'true' : 'false'}
                        className={`mentorship-tab ${
                            activeTab === 'progression' ? 'is-active' : ''
                        }`}
                        onClick={() => setActiveTab('progression')}
                    >
                        <BarChart3 size={16} /> Progression
                    </button>
                </nav>

                <div className="mentee-dashboard__tab-content">
                    {activeTab === 'history' &&
                        (loading ? (
                            <Skeleton width="100%" height="80px" borderRadius="12px" />
                        ) : !submissions || submissions.length === 0 ? (
                            <div className="mentee-dashboard__empty">
                                <h3>No submissions yet</h3>
                                <p>
                                    Click "New submission" to reflect on a framework module
                                    this quarter. The AI will give an initial assessment
                                    and your mentor will review.
                                </p>
                                <Button
                                    variant="primary"
                                    size="md"
                                    icon={<Plus size={16} />}
                                    onClick={() => setShowSubmitModal(true)}
                                >
                                    New submission
                                </Button>
                            </div>
                        ) : (
                            <ul className="mentee-dashboard__submission-list">
                                {submissions.map((s) => {
                                    const reportKey = `${s.module}-${s.quarter}`;
                                    const report = latestReportByModule.get(reportKey);
                                    return (
                                        <li key={s.id}>
                                            <Card
                                                variant="row"
                                                interactive={!!report}
                                                onClick={() => report && setOpenReport(report)}
                                            >
                                                <Card.Header>
                                                    <div className="mentee-dashboard__sub-info">
                                                        <div className="mentee-dashboard__sub-meta">
                                                            <span className="mentorship-chip mentorship-chip--module">
                                                                {MODULE_LABELS[s.module]}
                                                            </span>
                                                            <span className="mentee-dashboard__quarter">
                                                                {s.quarter}
                                                            </span>
                                                        </div>
                                                        <p className="mentee-dashboard__sub-state">
                                                            {s.state === 'finalized' && report
                                                                ? `Finalized v${report.version} · ${report.final_score}/5 · ${report.agreed_level}`
                                                                : s.state === 'reviewed'
                                                                ? 'Mentor review complete'
                                                                : s.state === 'assessed'
                                                                ? 'AI assessment ready · awaiting mentor'
                                                                : 'Submitted · awaiting AI assessment'}
                                                        </p>
                                                    </div>
                                                </Card.Header>
                                            </Card>
                                        </li>
                                    );
                                })}
                            </ul>
                        ))}

                    {activeTab === 'recommendations' && (
                        <RecommendationsList userId={user.id} />
                    )}

                    {activeTab === 'progression' && (
                        <ProgressionTimeline menteeId={user.id} />
                    )}
                </div>
            </div>

            {showSubmitModal && (
                <SubmissionModal
                    userId={user.id}
                    onClose={() => setShowSubmitModal(false)}
                    onSubmitted={handleSubmissionCreated}
                />
            )}
        </section>
    );
}

export default MenteeMentorshipView;
